import { createUseStyles } from 'react-jss';
import View from '@picsart/view/View';
import { Text, TextElementTypes } from '@picsart/text';
import {
  Backgrounds,
  BreakPoints,
  FontWeights,
  Spacings,
  Typography,
  mediaQuery,
} from '@picsart/cascade';
import {
  Button,
  ButtonSkinMarket,
  ButtonSkinStatusNegative,
  ButtonVariants,
} from '@picsart/button';
import IconUndo from '@picsart/icons/IconUndo';
import IconRedo from '@picsart/icons/IconRedo';
import IconCopy from '@picsart/icons/IconCopy';
import IconChevronDownLarge from '@picsart/icons/IconChevronDownLarge';
import IconChevronUpLarge from '@picsart/icons/IconChevronUpLarge';
import IconEditFilled from '@picsart/icons/IconEditFilled';
import { ButtonSkinSecondary } from '@picsart/button/skins';
import { useTranslation } from 'react-i18next';
import Snackbar from '@picsart/snackbar/Snackbar';
import IconInfoCircleOutline from '@picsart/icons/IconInfoCircleOutline';
import { clipboardWrite } from '@quicktools/shared-components';

export type PlaygroundEditorProps = {
  value: string;
  canDo: boolean;
  canRedo: boolean;
  isOpen?: boolean;
  error?: string | null;

  closeError?: () => void;
  undo: (steps?: number) => void;
  redo: (steps?: number) => void;
  setValue: (value: string) => void;
  onToggle?: () => void;
};

export default function PlaygroundEditor({
  undo,
  redo,
  value,
  canDo,
  isOpen,
  canRedo,
  onToggle,
  setValue,
  error,
  closeError,
}: PlaygroundEditorProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const charactersCount = value.length;
  const wordsCount = value.match(/\S+/g)?.length ?? 0;

  const handleCopyToClipboard = async function copyContent() {
    clipboardWrite(value);
  };

  return (
    <div
      className={styles.root}
      style={{
        flex: isOpen ? 1 : 'none',
        height: isOpen ? '100%' : 'auto',
      }}
    >
      <Button
        onClick={onToggle}
        skin={ButtonSkinSecondary}
        startIcon={IconEditFilled}
        className={styles.toggleBtn}
        endIcon={isOpen ? IconChevronDownLarge : IconChevronUpLarge}
      >
        Editor
      </Button>
      {isOpen ? (
        <View flex={1} flexDirection="column">
          <View
            gap={30}
            alignItems="center"
            padding={'8px 30px'}
            className={styles.header}
            justifyContent="space-between"
          >
            <Text
              className={styles.headerTitle}
              elementType={TextElementTypes.H3}
              size={Typography.t6}
              weight={FontWeights.Bold}
            >
              Editor
            </Text>

            <View flex="1" justifyContent="flex-end">
              <Button
                isDisabled={!canDo}
                centerIcon={IconUndo}
                variant={ButtonVariants.Text}
                skin={ButtonSkinStatusNegative}
                onClick={() => undo()}
              />
              <Button
                isDisabled={!canRedo}
                centerIcon={IconRedo}
                variant={ButtonVariants.Text}
                skin={ButtonSkinStatusNegative}
                onClick={() => redo()}
              />
            </View>
          </View>

          <View flex={1} flexDirection="column" position="relative">
            <textarea
              value={value}
              className={styles.textarea}
              onChange={(e) => setValue(e.target.value)}
            />

            {error ? (
              <Snackbar
                className={styles.error}
                actionIcon={IconInfoCircleOutline}
                handleClose={closeError}
                skin="warning"
              >
                {t(error)}
              </Snackbar>
            ) : null}
          </View>

          <View padding={'24px 30px'} justifyContent="space-between" alignItems="flex-end">
            <Text
              size={Typography.t4}
              elementType={TextElementTypes.Paragraph}
              color={Backgrounds.tint6}
            >
              {wordsCount} words / {charactersCount} characters
            </Text>

            <Button
              startIcon={IconCopy}
              skin={ButtonSkinMarket}
              isDisabled={!value.length}
              onClick={handleCopyToClipboard}
            >
              Copy
            </Button>
          </View>
        </View>
      ) : null}
    </div>
  );
}

const useStyles = createUseStyles(
  {
    root: {
      backgroundColor: Backgrounds.positive,
      display: 'flex',
      position: 'relative',

      [mediaQuery(BreakPoints.Small)]: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        flexDirection: 'column',
        zIndex: 10,
      },
    },
    header: {
      borderBottom: `1px solid ${Backgrounds.tint4}`,
    },
    headerTitle: {
      [mediaQuery(BreakPoints.Small)]: {
        display: 'none',
      },
    },
    toggleBtn: {
      top: '2%',
      right: '100%',
      position: 'absolute',
      transform: 'rotate(-90deg)',
      transformOrigin: '100% 100%',
      borderRadius: '8px 8px 0px 0px',
      [mediaQuery(BreakPoints.Small)]: {
        borderRadius: 0,
        position: 'static',
        right: 'none',
        top: 'none',
        transform: 'none',
        transformOrigin: 'none',
        justifyContent: 'space-between',
        '& :nth-child(2)': {
          marginLeft: 'auto',
        },
      },
    },
    textarea: {
      flex: 1,
      border: 'none',
      background: 'transparent',
      padding: 24,
      margin: 0,
      fontSize: 16,
      fontWeight: 500,
      color: '#000000',
    },
    error: {
      position: 'absolute',
      right: Spacings.s16,
      left: Spacings.s16,
      bottom: 80,
    },
  },
  { name: 'PlaygroundEditor' },
);
